import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { Layout } from "@/components/site/Layout";
import Home from "@/pages/Home";
import Contact from "@/pages/Contact";
import { FanMailPortal } from "@/components/site/ContactPortal";
import CheckStatus from "@/pages/CheckStatus";

import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import ChannelPrivacy from "@/pages/ChannelPrivacy";
import ChannelTerms from "@/pages/ChannelTerms";
import ChannelRestrictedRegions from "@/pages/ChannelRestrictedRegions";
import Complaints from "@/pages/Complaints";
import About from "@/pages/About";
import CaseReview from "@/pages/CaseReview";
import CustomPage from "@/pages/CustomPage";
import FilesPage from "@/pages/FilesPage";
import ContentFilesPage from "@/pages/ContentFilesPage";
import SubscribersPage from "@/pages/SubscribersPage";
import GalleryPage from "@/pages/GalleryPage";
import SendEmailPage from "@/pages/SendEmailPage";
import VerifyEmail from "@/pages/VerifyEmail";
import ChannelPage from "@/pages/ChannelPage";
import ChannelCheckoutPage from "@/pages/ChannelCheckoutPage";
import ChannelSupportPage from "@/pages/ChannelSupportPage";
import ChannelAccountAccessPage from "@/pages/ChannelAccountAccessPage";
import MaintenancePage from "@/pages/MaintenancePage";
import BankDetailsPage from "@/pages/BankDetailsPage";
import NotFound from "@/pages/NotFound";
import { COMING_SOON_FORCED, ComingSoon } from "@/components/site/ComingSoon";
import { AdminProvider, useAdmin } from "@/context/AdminContext";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import { ForgotPassword, ReportEmailChange, SetPassword, VerifyAccount } from "@/pages/PasswordPages";
import { AdminBar } from "@/components/admin/AdminBar";
import { MaintenanceProvider, useMaintenance } from "@/context/MaintenanceContext";
import { MaintenanceScreen } from "@/components/maintenance/MaintenanceScreen";
import { MaintenanceBanner, PreviewPill } from "@/components/maintenance/MaintenanceBanner";
import { WelcomeSplash } from "@/components/site/WelcomeSplash";

const ALWAYS_OPEN = ["/verify-email"];
const Shell = () => {
  const { user } = useAdmin();
  const { status } = useMaintenance();
  const { pathname } = useLocation();
  const isContactPortal = pathname === "/contact" || pathname === "/fan-mail" || pathname.startsWith("/contact/fan-mail/") || pathname === "/check-status";
  const isBankDetailsLink = pathname === "/bank-details/view";
  const [minDone, setMinDone] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setMinDone(true), 3000);
    return () => clearTimeout(timer);
  }, []);
  if (pathname.startsWith("/channel") || isContactPortal || isBankDetailsLink) return <Site />;
  return (
    <>
      <WelcomeSplash show={!minDone} />
      {minDone && <Site />}
    </>
  );
};

const PRIVATE = { "/login": Login, "/forgot-password": ForgotPassword, "/set-password": SetPassword, "/verify-account": VerifyAccount, "/report-email-change": ReportEmailChange, "/case-review": CaseReview, "/dashboard": Dashboard, "/bank-details/view": BankDetailsPage };

const Site = () => {
  const { user, canEdit } = useAdmin();
  const { status } = useMaintenance();
  const { pathname } = useLocation();
  const isContactPortal = pathname === "/contact" || pathname === "/fan-mail" || pathname.startsWith("/contact/fan-mail/") || pathname === "/check-status";
  if (pathname.startsWith("/contact/fan-mail/")) return <FanMailPortal />;
  if (pathname === "/channel/privacy") return <ChannelPrivacy />;
  if (pathname === "/channel/terms") return <ChannelTerms />;
  if (pathname === "/channel/restricted-regions") return <ChannelRestrictedRegions />;
  if (pathname === "/channel/account-access") return <ChannelAccountAccessPage />;
  if (pathname === "/channel/checkout") return <ChannelCheckoutPage />;
  const Private = PRIVATE[pathname.replace(/\/$/, "") || "/"];
  if (Private) return <Private />;
  if (pathname.startsWith("/channel/support")) return <ChannelSupportPage />;
  if (pathname === "/file-store") return <ContentFilesPage />;
  if (pathname.startsWith("/channel")) return <ChannelPage />;
  if ((COMING_SOON_FORCED || status?.coming_soon?.active) && !user && !isContactPortal) return pathname === "/verify-email" ? <div className="min-h-screen bg-[#FBF9F5]"><VerifyEmail /></div> : <ComingSoon />;
  if (!canEdit && status?.active && !ALWAYS_OPEN.includes(pathname) && !isContactPortal) return <MaintenanceScreen />;
  return (
    <>
      <Routes>
        <Route element={<Layout />}>
          <Route path="/" element={<Home />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/fan-mail" element={<FanMailPortal />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/complaints" element={<Complaints />} />
          <Route path="/about" element={<About />} />
          <Route path="/gallery" element={<GalleryPage />} />
          <Route path="/gallery/:folder" element={<GalleryPage />} />
          <Route path="/files" element={<FilesPage />} />
          <Route path="/mailing-list" element={<SubscribersPage />} />
          <Route path="/send-email" element={<SendEmailPage />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/maintenance" element={<MaintenancePage />} />
          <Route path="/check-status" element={<CheckStatus />} />
          <Route path="/:slug" element={<CustomPage />} />
          <Route path="*" element={<NotFound />} />
        </Route>
      </Routes>
      {!isContactPortal && <AdminBar />}
      {!isContactPortal && !canEdit && <MaintenanceBanner />}
      {!isContactPortal && <PreviewPill />}
    </>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AdminProvider>
        <MaintenanceProvider>
          <Shell />
          <Toaster position="top-center" />
        </MaintenanceProvider>
      </AdminProvider>
    </BrowserRouter>
  );
}

export default App;
